import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { useEffect, useState } from "react";
import { PreloadedQuery, usePreloadedQuery } from "react-relay";
import styled from "styled-components";
import { RightFrameMultipleChoiceElementPart_SubmittedAnswersQuery } from "@generated/RightFrameMultipleChoiceElementPart_SubmittedAnswersQuery.graphql";
import { MultipleChoiceAnswersDropdownsComponent } from "./multiplechoice-answers-dropdowns";
import { PrimaryButton } from "../../../infecto-lms-webapp/components/buttons/PrimaryButton";
import { StripHtml } from "../../../infecto-lms-webapp/functions/helpers/StripHtml";
import { LMS_WEBAPP_CONFIG_IMPL } from "../../../infecto-lms-webapp-impl/config";
import { SUBMITTED_ANSWERS_QUERY } from "../right-frame/RightFrameMultipleChoiceElementPart";
import { EditIcon } from "../right-frame/RightFrameSvgIcons";

interface OwnProps {
	isVisible: boolean;
	onHideDialog(): void;
	queryRef: PreloadedQuery<
		RightFrameMultipleChoiceElementPart_SubmittedAnswersQuery,
		Record<string, unknown>
	>;
	submitLearnElement(): void;
	totalElementsCount: number;
	currentElement: any; //eslint-disable-line @typescript-eslint/no-explicit-any
	checkedAnswerIndices: number[];
	contentSubmissionId: string;
	setCheckedAnswerIndices: (indices: number[]) => void;
	reloadSubmittedAnswersQuery(): void;
}

export function ShowEditableUserAnswersSummaryDialog({
	isVisible,
	onHideDialog,
	queryRef,
	submitLearnElement,
	totalElementsCount,
	currentElement,
	checkedAnswerIndices,
	contentSubmissionId,
	setCheckedAnswerIndices,
	reloadSubmittedAnswersQuery,
}: OwnProps) {
	const data = usePreloadedQuery<RightFrameMultipleChoiceElementPart_SubmittedAnswersQuery>(
		SUBMITTED_ANSWERS_QUERY,
		queryRef,
	);

	const [editingIndexArray, setEditingIndexArray] = useState<number[]>([]);

	const answersRenderer = (answers: readonly string[]): JSX.Element[] => {
		return answers?.map((answer, index) => <p key={index}>{StripHtml(answer)}</p>);
	};
	useEffect(() => {
		setEditingIndexArray([]);
	}, []);
	const itemRenderer = (
		index: number,
		question: string,
		answers: JSX.Element[],
		elementId: string,
		checkedAnswers: readonly number[],
	): JSX.Element => {
		return (
			<div className="mt-4" key={index}>
				<span className="font-bold text-sm md:text-base">
					<span>{index + 1}. Frage:</span> {StripHtml(question)}
				</span>
				<br />
				<br />
				<div className="flex flex-row align-items-baseline mb-3">
					<span className="text-sm md:text-base">Ihre Antwort:</span>
					{!editingIndexArray.includes(index) ? (
						<div className="flex flex-row align-items-center">
							<span className="text-sm md:text-base ml-4 mb-2">{answers}</span>
							<div
								onClick={() => {
									if (!editingIndexArray.includes(index)) {
										setEditingIndexArray([...editingIndexArray, index]);
									}
								}}
								className="cursor-pointer ml-2"
							>
								<EditIcon />
							</div>
						</div>
					) : (
						<div className="mb-3">
							<MultipleChoiceAnswersDropdownsComponent
								reloadSubmittedAnswersQuery={reloadSubmittedAnswersQuery}
								questionIndex={index}
								editingIndexArray={editingIndexArray}
								setEditingIndexArray={setEditingIndexArray}
								isLastQuestion={index == totalElementsCount - 1}
								elementId={elementId}
								contentSubmissionId={contentSubmissionId}
								checkedAnswers={
									index == totalElementsCount - 1 ? checkedAnswerIndices : checkedAnswers
								}
								setLastAnswerCheckedAnswerIndices={setCheckedAnswerIndices}
							/>
						</div>
					)}
				</div>
			</div>
		);
	};

	const contentRenderer = data.LearnV2.GetSubmittedAnswers.map((decision, index) =>
		itemRenderer(
			index,
			decision.question,
			answersRenderer(decision.answers),
			decision.elementId || "",
			decision.answerIndices,
		),
	);

	if (data.LearnV2.GetSubmittedAnswers.length < totalElementsCount) {
		contentRenderer.push(
			itemRenderer(
				totalElementsCount - 1,
				currentElement?.question,
				answersRenderer(
					currentElement?.answerOptions
						.filter((e: any, index: number) => checkedAnswerIndices.includes(index)) //eslint-disable-line @typescript-eslint/no-explicit-any
						.map((e: any) => e.answer), //eslint-disable-line @typescript-eslint/no-explicit-any
				),
				currentElement.id,
				checkedAnswerIndices,
			),
		);
	}

	const onSubmitLearnElementAndHideDialog = () => {
		submitLearnElement();
		onHideDialog();
	};

	const goBackButton = () => {
		onHideDialog();
		contentRenderer.pop();
	};
	const footer = () => {
		return (
			<div>
				<GoBackButton
					className={`text-black-alpha-90 mr-2 md:mr-4 h-full p-button-outlined p-button-secondary bg-white`}
					label="ZURÜCK"
					onClick={goBackButton}
				/>
				<CustomButtonDialog
					color={LMS_WEBAPP_CONFIG_IMPL.brandColor}
					label="ABSENDEN"
					onClick={onSubmitLearnElementAndHideDialog}
				/>
			</div>
		);
	};

	return (
		<StyledDialog
			header="Zusammenfassung der Anworten"
			visible={isVisible}
			footer={footer}
			onHide={onHideDialog}
		>
			{contentRenderer}
		</StyledDialog>
	);
}

const StyledDialog = styled(Dialog)`
	width: 95%;

	.p-dialog-header {
		color: black !important;
	}
	.p-dialog-content {
		color: black !important;
	}
`;
export interface ButtonDialogProps {
	color: string;
}

export const CustomButtonDialog = styled(PrimaryButton)<ButtonDialogProps>`
	background-color: ${(props) => props.color} !important;
	border-color: ${(props) => props.color} !important;
	&.p-button:hover {
		background-color: ${(props) => props.color};
		border-color: ${(props) => props.color};
	}
`;

const GoBackButton = styled(Button)`
	padding: 10px 20px;
`;
